/**
 * The descent's own reads ahead of the tile stream: the claim's region field
 * and its survey, the plot a body with no site gets from its own leaves, the
 * focus warm and the ring ahead, all ordered on one queue.
 */

import type { AtlasLive, AtlasState, Site } from '../atlas';
import { urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import { isSurveyed } from './rover.ts';
import {
  loadPlotField, type DataTier, type PlotField, type SubPlot,
} from './plotField.ts';
import { DTM_TILE_BASE, dtmUrl } from './dtmBase.ts';
import { createDescentQueue, DESCENT_RUNG, type DescentQueue } from './descentQueue.ts';
import { parsePyramidManifest, pyramidUrl, rolesForTile, tileUrl } from './tileSource.ts';
import { gsd as heightGsd, neighboursOf, tileForLatLon, type LoadedTile } from './tileTree.ts';
import { buildPyramidPlotField, lumaAt, type PyramidAlbedoProbe } from './pyramidPlot.ts';
import { albedoByteTable, albedoTransferOf, linearToSrgbByte } from './albedoTransfer.ts';
import { readableCanvas2d } from './canvas2d.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import type { SiteChipsHandle } from './panels/siteChips.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { DescentAnchorHandle } from './descentAnchor.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { FrameGovernorHandle } from './frameGovernor.ts';
import type { OverlayViewHandle } from './overlayView.ts';
import type { PlotBuildHandle } from './plotBuild.ts';
import type { PlotSunHandle } from './plotSun.ts';
import type { SelectionHandle } from './selection.ts';
import type { SiteGroundHandle } from './siteGround.ts';
import type { SiteRegistryState } from './siteRegistry.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';
import type { WorldHostState } from './worldHost.ts';

/** What a ring ahead is read around: a surveyed site's centre, or the landing
 *  point on a body with no site. `id` is what `ringSite` and `ringDone` hold. */
interface RingTarget {
  readonly id: string;
  readonly body: string;
  readonly lat: number;
  readonly lon: number;
}

export interface DescentLoadDeps {
  readonly frameGovernor: FrameGovernorHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly focusRig: FocusRigHandle;
  readonly plotSun: PlotSunHandle;
  site(): Site | undefined;
  siteUrl(): string | null;
  readonly state: AtlasState;
  readonly siteRegistry: SiteRegistryState;
  readonly worldHostState: WorldHostState;
  readonly live: AtlasLive;
  /** Built after this module: each arrives as a getter. */
  readonly solarScrub: SolarScrubHandle;
  readonly selection: SelectionHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly plotBuild: PlotBuildHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly siteGround: SiteGroundHandle;
  readonly siteChips: SiteChipsHandle;
  readonly overlayView: OverlayViewHandle;
}

/** The loader's own state that the mount, the tick and the probe read or write. */
export interface DescentLoadState {
  readonly plotLoading: boolean;
  claimPriorityOff: boolean;
  claimTierLate: boolean;
  pyramidPlotBody: string | null;
  pyramidPlotLevel: number | null;
  pyramidPlotHoles: number;
  pyramidPlotFor: { lat: number; lon: number } | null;
  descentFor: { body: string; lat: number; lon: number } | null;
  readonly pyramidPlotBuilding: boolean;
  pyramidPlotRetryMs: number;
  pyramidPlotNextTryMs: number;
  claimAskedAtTier: number | null;
  readonly claimStartAltM: number | null;
  claimWantUrl: string | null;
  readonly claimQueuedUrl: string | null;
  readonly warmBody: string | null;
  readonly warmSiteId: string | null;
  warmSettle: ReturnType<typeof setTimeout> | null;
  readonly warmAskedAltM: number | null;
  readonly warmAskedMs: number | null;
  readonly warmClaimReadyMs: number | null;
  landPressMs: number | null;
  readonly ringSite: string | null;
  readonly ringCommitted: string | null;
  ringAtSurface: { warmTiles: number; tiles: number } | null;
  readonly ringAtPress: 'none' | 'kept' | 'started' | 'retargeted' | 'off';
  readonly landAskFound: 'done' | 'in-flight' | 'other-site' | 'cold';
  shipTrack: { bodyId: string; lat: number; lon: number } | null;
  claimUrl: string | null;
  claimGen: number;
  plotError: string | null;
}

export interface DescentLoadHandle {
  loadClaim(url: string): Promise<void>;
  surveyRings(field: PlotField): number;
  ensurePyramidPlot(): Promise<void>;
  claimHoldLevel(): number | undefined;
  releaseGroundRing(): void;
  prefetchDescent(): void;
  arcDeg(lat1: number, lon1: number, lat2: number, lon2: number): number;
  ringTilesUnder(body: string, lat: number, lon: number): string[];
  armGroundWarm(): void;
  readonly ringHoldOff: boolean;
  readonly ringUnbudgeted: boolean;
  readonly ringHole: boolean;
  readonly ringChurn: boolean;
  readonly fetchAtCrossing: boolean;
  readonly prefetchOff: boolean;
  readonly focusWarmOff: boolean;
  readonly ringAheadOff: boolean;
  readonly pressRingOff: boolean;
  readonly pyramidRingOff: boolean;
  readonly warmWrongSite: boolean;
  readonly noWantClear: boolean;
  readonly noReleaseClear: boolean;
  readonly noClaimRetarget: boolean;
  readonly noWantRestore: boolean;
  readonly pyramidPlotOff: boolean;
  readonly pyramidPlotFlat: boolean;
  readonly plotKeepBody: boolean;
  readonly plotReleaseOnLand: boolean;
  readonly warmSpend: { requests: number; bytes: number; tiles: number };
  readonly ringSpend: { requests: number; bytes: number; tiles: number };
  readonly ringDone: Set<string>;
  readonly descentQueue: DescentQueue;
  readonly state: DescentLoadState;
}

/** A tile's own drawn albedo, decoded to pixels once so `lumaAt` can sample it. */
function pixelsOf(tile: LoadedTile): ImageData | null {
  const bitmap = tile.albedo?.source;
  if (!(bitmap instanceof ImageBitmap)) return null;
  const made = readableCanvas2d(bitmap.width, bitmap.height);
  if (!made) return null;
  made.ctx.drawImage(bitmap, 0, 0);
  return made.ctx.getImageData(0, 0, bitmap.width, bitmap.height);
}

export function createDescentLoad(deps: DescentLoadDeps): DescentLoadHandle {
  const { site, siteUrl, state } = deps;
  const { loadStarted, markLoad, loadFinished } = deps.frameGovernor;
  const { leafDatumM } = deps.surfaceRadius;
  const { bodyClimate, ladderBody, smoothGround } = deps.bodyReaders;
  const { altitude } = deps.focusRig;
  const { applySun } = deps.plotSun;

  let plotLoading = false;
  /**
   * `--ship --inject claim-no-priority`: the claim's two reads go out with no
   * hold on the tile queue, which is the race the descent used to lose.
   */
  let claimPriorityOff = TEST_SWITCHES && urlFlag('claimnopriority');
  /**
   * `--ship --inject ring-first`: the hold keeps no level off the pipe.
   *
   * The cap alone, on the ceiling it had before the claim's own transfer was
   * measured against it - the ring downloading its deep levels beside a claim
   * that has not landed, which is what the descent used to do.
   */
  const ringHoldOff = TEST_SWITCHES && urlFlag('ringfirst');
  /**
   * `--ship --inject ring-unbudgeted`: the relief arc drives refinement again.
   *
   * The descent's whole request count back on the rule that asked for a level
   * of detail everywhere a peak could stand rather than everywhere the eye can
   * reach. Read where the tree is opened, so it is a boot flag.
   */
  const ringUnbudgeted = TEST_SWITCHES && urlFlag('ringunbudgeted');
  /**
   * `--ship --inject ring-hole`: drops the one drawn tile nearest the split
   * horizon's own grazing edge, every request, so the horizon-band reading
   * has a known hole to catch. Read where the tree is opened, a boot flag.
   */
  const ringHole = TEST_SWITCHES && urlFlag('ringhole');
  /**
   * `--ship --inject ring-churn`: the tile queue as it was before this lane.
   *
   * An in-flight request that falls out of the top of the order is abandoned
   * whatever it has downloaded, and a hole is ordered by its level alone. Read
   * where the tree is opened, so it is a boot flag.
   */
  const ringChurn = TEST_SWITCHES && urlFlag('ringchurn');
  /** `--ship --inject claim-tier-late`: the claim asked for at tier 4 again. */
  let claimTierLate = TEST_SWITCHES && urlFlag('claimtierlate');
  /**
   * `--ship --inject fetch-at-crossing`: every read a commit starts put back
   * at the altitude crossing that used to start it.
   *
   * The whole of loading ahead in one switch - no focus warm, no claim at the
   * press and no ring ahead of the stream - so the descent's reads begin where
   * `applyState`'s own rung gate begins them and nowhere earlier.
   */
  const fetchAtCrossing = TEST_SWITCHES && urlFlag('fetchatcrossing');
  /** `--ship --inject no-prefetch`: a LAND commit starts nothing of its own. */
  const prefetchOff = TEST_SWITCHES && urlFlag('noprefetch') || fetchAtCrossing;
  /** `--ship --inject no-focus-warm`, `?nofocuswarm=1`: focusing a body starts
   *  nothing, so the ground's reads wait for the LAND press. */
  const focusWarmOff = TEST_SWITCHES && urlFlag('nofocuswarm') || fetchAtCrossing;
  /** The ring ahead is off with the rest of loading ahead, and only with it. */
  const ringAheadOff = fetchAtCrossing;
  /**
   * `--ship --inject no-press-ring`: the press keeps no ring and starts none.
   *
   * The press side of the ring alone, deleted. The claim still goes out at the
   * press and the queue still orders it, so a ring count red under this is the
   * ring's own and not the claim's rung; the arm pairs it with `nofocuswarm`
   * so the press is the only thing that could have read a ring.
   */
  const pressRingOff = TEST_SWITCHES && urlFlag('nopressring');
  /** `--ship --inject no-pyramid-ring`: a body with no surveyed site gets no
   *  ring under its landing point, from the focus or the press. */
  const pyramidRingOff = TEST_SWITCHES && urlFlag('nopyramidring');
  /** `--ship --inject warm-wrong-site`: the warm aimed at a site the press will
   *  not resolve, which is what the yield in `loadClaim` is for. */
  const warmWrongSite = TEST_SWITCHES && urlFlag('warmwrongsite');
  /** `--ship --inject no-want-clear`: `releaseGroundWarm` bumps `claimGen` for
   *  an in-flight claim but leaves `claimWantUrl` pointed at it, so a same-url
   *  re-ask silently no-ops in `loadClaim`'s own dedup check instead of
   *  queuing a fresh fetch. */
  const noWantClear = TEST_SWITCHES && urlFlag('nowantclear');
  /** `--ship --inject no-release-clear`: the same staleness in `releasePlot`,
   *  crossed by a ladder body change rather than a focus loss. */
  const noReleaseClear = TEST_SWITCHES && urlFlag('noreleaseclear');
  /** `--ship --inject no-claim-retarget`: a read already in flight swallows the
   *  ask outright, the way the guard at the top of `loadClaim` used to, so a
   *  site switch mid-claim neither retargets nor queues. */
  const noClaimRetarget = TEST_SWITCHES && urlFlag('noclaimretarget');
  /** `--ship --inject no-want-restore`: `loadClaim` leaves the want cleared by
   *  its own mid-read `releasePlot`, so its survey abandons at the first cell
   *  and the SURFACE build has no rows to adopt. */
  const noWantRestore = TEST_SWITCHES && urlFlag('nowantrestore');
  /**
   * `--ship --inject pyramid-floor`: a body with no site gets no plot built
   * from its leaves, so the descent ends on the pyramid's floor the way it did
   * before this path existed and the walkaround never mounts.
   */
  const pyramidPlotOff = TEST_SWITCHES && urlFlag('pyramidplotoff');
  /** `--stand --inject smooth-plot`: smooth ground gets a plot built from its
   *  leaves again, so the walkaround stands on that mesh at its 5 m mast. */
  const smoothPlotOld = TEST_SWITCHES && urlFlag('smoothplot');
  /**
   * `--ship --inject pyramid-plot-flat`: every sample of the patch answers at
   * the datum, so the field is a plane where the leaves under it carry relief
   * and the arm's relief reading has to redden.
   */
  const pyramidPlotFlat = TEST_SWITCHES && urlFlag('pyramidplotflat');
  /** `--ship --inject plot-keep-body`: a patch built from the leaves is kept
   *  across any LAND back onto its body, wherever the ship has moved to. */
  const plotKeepBody = TEST_SWITCHES && urlFlag('plotkeepbody');
  /** `--ship --inject plot-release-on-land`: every ladder body change releases
   *  the patch, a LAND back onto the ground it was built for included. */
  const plotReleaseOnLand = TEST_SWITCHES && urlFlag('plotreleaseonland');
  /** `--ship --inject warm-pull`: cruise warms the strongest pull's ground
   *  rather than the ground of the body LAND is offering. */
  const warmPull = TEST_SWITCHES && urlFlag('warmpull');
  /** `--stand --inject no-leaf-ortho`: a leaf-built plot gets no orthoimage,
   *  so its ground stays the flat tint it had before this path existed. */
  const leafOrthoOff = TEST_SWITCHES && urlFlag('leaforthooff');
  /** The body the loaded field was built from leaves for, and the coarsest
   *  level that answered; null whenever the field came from a bake. */
  let pyramidPlotBody: string | null = null;
  let pyramidPlotLevel: number | null = null;
  let pyramidPlotHoles = 0;
  /** The point `goTo` was handed for the descent that built the loaded patch,
   *  and for the descent now open; null where the focus named no point. */
  let pyramidPlotFor: { lat: number; lon: number } | null = null;
  let descentFor: { body: string; lat: number; lon: number } | null = null;
  /** True while a patch is being sampled, so the per-frame ask does not stack. */
  let pyramidPlotBuilding = false;
  /** A pass that cannot resolve the patch waits before the next one: it walks
   *  16,384 probes against the frame budget first, and a body whose leaves keep
   *  it under the bar would otherwise pay that on every frame of the descent. */
  const PYRAMID_PLOT_RETRY_MIN_MS = 250;
  const PYRAMID_PLOT_RETRY_MAX_MS = 4000;
  let pyramidPlotRetryMs = 0;
  let pyramidPlotNextTryMs = 0;
  /** The rung the first claim of this page was asked at, and the altitude the
   *  read actually started from. `--ship`'s `claim-ask` bar. */
  let claimAskedAtTier: number | null = null;
  let claimStartAltM: number | null = null;
  /**
   * The claim the page WANTS, beside the one that is loading.
   *
   * A focus warm can be reading one site's field when a press resolves
   * another. That read cannot be recalled, but it can be abandoned at its next
   * slice and the wanted one started in its place, which is what these two
   * carry: `claimUrl` is what is loaded, `claimWantUrl` what should be.
   */
  let claimWantUrl: string | null = null;
  let claimQueuedUrl: string | null = null;
  /** The in-flight claim's own reads. Dropping the RESULT of a superseded read
   *  still leaves its megabytes in front of the one that replaced it, so a
   *  retarget abandons the requests too. */
  let claimAbort: AbortController | null = null;
  /**
   * The focus warm: what the ground's reads were started for, and what they
   * spent, before any press.
   *
   * `body` is the body the warm is aimed at and `siteId` the site on it; the
   * generation is what an abandoned warm's own continuations check, since a
   * fetch already on the wire outlives the focus that asked for it.
   */
  let warmBody: string | null = null;
  let warmSiteId: string | null = null;
  let warmClaimUrl: string | null = null;
  let warmSettle: ReturnType<typeof setTimeout> | null = null;
  let warmGen = 0;
  /** The ring ahead's own reads, whichever of the two started them. */
  let ringAbort: AbortController | null = null;
  // The Atlas camera's own altitude, not the ship's; meaningful only once the
  // Atlas has actually navigated to the warmed body, so it is null otherwise
  // rather than the camera's default solar-system-wide reading.
  let warmAskedAltM: number | null = null;
  let warmAskedMs: number | null = null;
  let warmClaimReadyMs: number | null = null;
  let landPressMs: number | null = null;
  /** What a focus has spent on ground the player may never land on. */
  const warmSpend = { requests: 0, bytes: 0, tiles: 0 };
  /** What the ring ahead has spent since the press, on ground now committed. */
  const ringSpend = { requests: 0, bytes: 0, tiles: 0 };
  /** The `RingTarget.id` the ring ahead is reading for, while a read of it is running. */
  let ringSite: string | null = null;
  /** Rings read through to their last tile, by `RingTarget.id`. */
  const ringDone = new Set<string>();
  /**
   * The site a LAND press resolved, while its ring is the descent's own.
   *
   * A ring reading for this is the ground being landed on and is the rung
   * above the tile stream, so it reads through rather than standing down for
   * it; a ring for any other site is a focus warm on ground nobody committed
   * to and still stands down.
   */
  let ringCommitted: string | null = null;
  /**
   * What the ring had read by the SURFACE crossing, split the way the ledger
   * splits it: the focus's tiles and the press's. Taken once, at the crossing,
   * because a committed ring goes on reading past it.
   */
  let ringAtSurface: { warmTiles: number; tiles: number } | null = null;
  /** What the press found the ring ahead doing, for `--ship`'s own readback. */
  let ringAtPress: 'none' | 'kept' | 'started' | 'retargeted' | 'off' = 'none';
  /**
   * What the press's own ask found: the warm's claim already loaded, the same
   * read still in flight, a warm pointed at another site, or nothing warmed.
   */
  let landAskFound: 'done' | 'in-flight' | 'other-site' | 'cold' = 'cold';
  /** Where the ship is and what it is over, as the LAND affordance reads it.
   *  Null while no ship is in a band that offers a landing. */
  let shipTrack: { bodyId: string; lat: number; lon: number } | null = null;
  /**
   * Longest a claim read may hold the stream down, however it is going.
   *
   * Over the claim's own transfer and not under it: 4.33 MB of height and
   * orthoimage over the 1.5 Mbit pipe the descent dojo models is 23 s on its
   * own and about 33 s with the coarse ring still beside it, and the 20 s this
   * used to be expired mid-download and handed the rest of the descent's pipe
   * back to the ring. Ninety covers a link a third of that one's speed. The
   * ceiling is for a read that never lands, not for a slow one, and the levels
   * at and under `CLAIM_HOLD_MAX_LEVEL` are never held at all, so the ring is
   * off the wire for no part of this.
   */
  const CLAIM_HOLD_CEILING_MS = 90_000;
  /**
   * The deepest tile level the stream may reach while a claim read is out.
   *
   * The walkaround's base mesh is built from the claim's own height and
   * orthoimage and wants no tile at all, so every tile the ring takes before
   * the mesh is up is either the coarse picture under a descending camera or
   * ground that mesh is about to cover. Levels 0 and 1 are that picture - a
   * body's whole globe at 2 and 8 tiles across a face - and they cost 1.80 MB
   * of the 7.87 MB the ring spent inside the claim's own window on a cold
   * throttled descent, so holding everything below them is what hands the claim
   * the pipe rather than a third of it.
   */
  const CLAIM_HOLD_MAX_LEVEL = 1;
  /** The ceiling before the claim's transfer was measured against it; the
   *  control's, so `--inject ring-first` is the pipe exactly as it was. */
  const RING_FIRST_CEILING_MS = 20_000;
  /**
   * Longest one read of the ring ahead may keep the stream waiting.
   *
   * A single tile role at level 2 or 3 is tens to hundreds of kilobytes, which
   * is seconds on the 1.5 Mbit pipe the descent dojo models and nothing on a
   * real one; twenty covers a link several times slower than that without a
   * stalled read parking the rest of the ring behind it.
   */
  const RING_AHEAD_CEILING_MS = 20_000;
  /**
   * The descent's own reads, in one order.
   *
   * The claim first, then the ring around the committed site, then the tile
   * stream - which the queue holds rather than carries, since the tree keeps
   * its own traversal order. The ring rung does not hold the stream at all: it
   * only runs while the stream has not started, so there is nothing there to
   * hold, and standing the ring down is `readGroundRing`'s own `tilesActive`
   * check rather than a clamp.
   */
  const descentQueue: DescentQueue = createDescentQueue({
    rungs: [
      {
        name: 'claim',
        maxInFlight: 4,
        ceilingMs: ringHoldOff ? RING_FIRST_CEILING_MS : CLAIM_HOLD_CEILING_MS,
        holdsStream: true,
        holdMaxLevel: ringHoldOff ? null : CLAIM_HOLD_MAX_LEVEL,
      },
      {
        name: 'ring',
        maxInFlight: 1,
        ceilingMs: RING_AHEAD_CEILING_MS,
        holdsStream: false,
        holdMaxLevel: null,
      },
    ],
    onHold: (holding, maxLevel) => {
      deps.pyramidView.tileTree?.setFetchHold(holding, maxLevel ?? undefined);
    },
  });
  /** Which region the loaded field belongs to, so it is fetched once. */
  let claimUrl: string | null = null;
  /** Bumped on every claim release or target change; a `loadClaim` fetch
   *  that outlives the bump discards rather than publishes. */
  let claimGen = 0;
  let plotError: string | null = null;

  /**
   * The claim's data model: the region field and its sub-plot survey.
   *
   * What CLAIM and PLOT need out of a region bake is the NUMBERS - the
   * placements, each cell's slope, roughness and insolation verdict, the
   * addresses - because the terrain they draw is the pyramid's own leaves. The
   * whole-file MESH is the walkaround's terrain and is built on the SURFACE
   * edge (`ensurePlotMesh`), so no gated leg of the descent pays for a
   * 500k-vertex lattice, a 33 MB height texture or the orthoimage.
   */
  async function loadClaim(url: string): Promise<void> {
    // A read for other ground is in flight: it is abandoned rather than
    // yielded to. Yielding kept the ask, but behind a download the player has
    // already changed their mind about - a minute of region on a slow link,
    // most of it after the landing is committed. The read that replaces it is
    // started in the `finally` below, a frame later.
    //
    // Checked before `claimUrl === url` and not after, because pressing the
    // loaded region again while another one is on the wire is the same
    // problem: nothing needs re-reading, but the read still out is for ground
    // nobody wants and it would replace what is under the player when it lands.
    if (plotLoading) {
      if (noClaimRetarget) {
        // The control: the pre-fix swallow, minus the abort and the read that
        // replaces it. The token bump still makes the read in flight drop itself.
        if (claimWantUrl !== url) { claimWantUrl = url; claimQueuedUrl = url; claimGen += 1; }
        return;
      }
      // A retarget is also a target change for the guard's own token, so the
      // in-flight read drops itself on the same check a release uses instead
      // of carrying a second comparison against `claimWantUrl` here.
      if (claimWantUrl !== url) {
        claimWantUrl = url;
        claimQueuedUrl = claimUrl === url ? null : url;
        claimGen += 1;
        claimAbort?.abort();
      }
      return;
    }
    if (claimUrl === url) return;
    claimWantUrl = url;
    if (claimStartAltM === null) claimStartAltM = altitude();
    plotLoading = true;
    plotError = null;
    loadStarted();
    deps.overlayView.renderOverlay();
    // Captured now: `claimUrl` is not yet `url`, so it cannot stand in for
    // "has the ladder moved on" the way it does further down this function.
    const gen = claimGen;
    const ctrl = new AbortController();
    claimAbort = ctrl;
    // The claim's own two reads take the pipe ahead of the pyramid until they
    // land: 4.5 MB of height and orthoimage against a stream that had 900-odd
    // tiles in flight, which is the race a descent used to outrun. Released on
    // the orthoimage rather than on the field, since that image is the SURFACE
    // build's first await.
    const releaseHold = holdTileFetches();
    let ortho: Promise<ImageBitmap> | null = null;
    try {
      const field = await loadPlotField(url, { worker: deps.pyramidView.tileWorker(), signal: ctrl.signal });
      ortho = field.albedoPromise ?? null;
      markLoad();
      if (deps.live.disposed) return;
      if (gen !== claimGen) {
        // A body or site change released the claim, or a retarget while this
        // was in flight bumped the token above; drop the bitmap the way
        // `releasePlot` drops a superseded `plotField` rather than publish it
        // for a ladder that no longer wants it.
        if (!field.albedoConsumed) {
          const droppedField = field;
          void droppedField.albedoPromise
            ?.then((bitmap) => { if (!droppedField.albedoConsumed) bitmap.close(); })
            .catch(() => {});
        }
        return;
      }
      // The mesh under the old claim is not this ground, and `ensurePlotMesh`
      // will not replace one that is already up.
      deps.plotBuild.releasePlot();
      claimUrl = url;
      // That release clears `claimWantUrl`, and the ask it clears is this
      // read's own: unrestored, the survey below abandons at its first cell.
      if (!noWantRestore) claimWantUrl = url;
      deps.live.plotField = field;
      // The survey, across frames rather than in one. Round 10 measured it at
      // 30.7 ms of arithmetic plus 12.6 ms of collection behind it, landing in
      // the frame the field decodes - which is inside REGIONAL->LOCAL, and is
      // that leg's whole overrun on the ship arm. Published only once it is
      // whole, so nothing reads a half-surveyed claim.
      const subs = await surveyPlotField(field,
        () => !deps.live.disposed && claimUrl === url && claimWantUrl === url);
      // A site or body change mid-survey abandons this load; release the
      // hold now rather than waiting on the orthoimage or the ceiling.
      if (subs === null) { releaseHold(); return; }
      deps.live.plotSubs = subs;
      markLoad();
      deps.live.selectedSub = null;
      deps.selection.applySelection();
      void deps.siteGround.resolveAddresses();
    } catch (e) {
      // A read this function abandoned is not a failure to put in front of the
      // player: the one that replaced it owns the overlay now.
      if (!ctrl.signal.aborted) plotError = e instanceof Error ? e.message : String(e);
    } finally {
      if (claimAbort === ctrl) claimAbort = null;
      // The survey is published here; the orthoimage is not, so the hold runs
      // past this block rather than ending with it.
      if (ortho) void ortho.then(releaseHold, releaseHold);
      else releaseHold();
      plotLoading = false;
      loadFinished();
      // A dispose mid-await must not touch the sky or write into detached DOM.
      if (deps.live.disposed) return;
      // The read that was waiting on this one, started the moment the pipe is
      // free rather than at the next rung the ladder happens to cross.
      const queued = claimQueuedUrl;
      claimQueuedUrl = null;
      if (queued !== null && queued !== claimUrl) void loadClaim(queued);
      applySun();
      deps.overlayView.renderOverlay();
    }
  }

  /** Survey depth: a region carries many parent plots, a single bake one. */
  function surveyRings(field: PlotField): number {
    return field.placements.length > 4 ? 2 : 3;
  }

  /**
   * The cell survey both plot sources share, sliced across frames.
   *
   * Null where `current` went false mid-slice: the ground this survey is about
   * has been given back, and a half-scored list must not be published for the
   * one that replaced it.
   */
  async function surveyPlotField(
    field: PlotField, current: () => boolean,
  ): Promise<SubPlot[] | null> {
    const slice = deps.plotBuild.frameSlices();
    const subs: SubPlot[] = [];
    for (const cell of field.surveyCells(surveyRings(field))) {
      subs.push(cell);
      await slice();
      if (!current()) return null;
    }
    // The rover's survey memory outlives the walkaround, so a cell surveyed
    // in an earlier session reads SURVEYED at the region rather than dropping
    // back to its orbital band until the rover is deployed again.
    for (const p of subs) if (isSurveyed(p.id)) p.dataTier = 2 as DataTier;
    const climate = bodyClimate();
    if (climate) {
      for (const _ of field.scoreCells(subs, deps.solarScrub.solar.lsDeg, climate)) {
        await slice();
        if (!current()) return null;
      }
    }
    return subs;
  }

  /**
   * The drawn leaves' own imagery under a build's patch, as luminance: the
   * deepest leaf `tileAlbedoAt` finds, decoded to pixels once per source tile
   * and kept only for this one build.
   */
  function leafAlbedoSampler(): PyramidAlbedoProbe {
    const cache = new Map<LoadedTile, ImageData | null>();
    // The ortho is read as an sRGB byte by the plot shader, so a linear
    // product's reflectance goes back in as the byte that decodes to it.
    const transfer = albedoTransferOf(deps.bodyReaders.rowById(deps.pyramidView.tileBody));
    const table = transfer ? albedoByteTable(transfer) : undefined;
    return (lat, lon) => {
      const at = deps.pyramidView.tileAlbedoAt(lat, lon);
      if (at === null) return undefined;
      let px = cache.get(at.from);
      if (px === undefined) {
        px = pixelsOf(at.from);
        cache.set(at.from, px);
      }
      if (px === null) return undefined;
      const v = lumaAt(px, at.s, at.t, table);
      return v === null || !table ? v : linearToSrgbByte(v);
    };
  }

  /**
   * The plot a body with no surveyed region stands on, from the leaves the
   * descent already has under the landing point.
   *
   * Nineteen of the twenty-one bodies the manifest chips "ground to SURFACE"
   * have no row in `sites.json`, so `siteUrl()` is null, `loadClaim` never
   * runs and there was no field to build a mesh from: the ladder ended at the
   * pyramid's floor and the walkaround never mounted. The claim path is
   * untouched - this runs only where there is no claim to ask for.
   *
   * Asked once a frame from the descent, because which leaves are resident is
   * a fact about the ring rather than about the rung: the patch is built as
   * soon as the ground under the landing point is there, and rebuilt once if a
   * finer level lands before the mesh does.
   */
  async function ensurePyramidPlot(): Promise<void> {
    if (pyramidPlotOff || deps.live.disposed || pyramidPlotBuilding || plotLoading) return;
    if (performance.now() < pyramidPlotNextTryMs) return;
    const body = ladderBody();
    if (body === null || body !== deps.pyramidView.tileBody || !deps.siteRegistry.sitesLoaded || site() !== undefined) return;
    // Smooth ground stands on the tiles themselves; its only relief is drawn by
    // the mesh, which `tileHeightProbe` answers for the focus anchor, not for a plot.
    if (smoothGround(body) && !smoothPlotOld) return;
    if (plotError !== null || deps.pyramidView.tileDrawn.size === 0) return;
    // A finer level than the one the loaded patch was built from is an upgrade
    // while there is still no mesh; once the walkaround is up the ground under
    // the player does not move.
    const loaded = pyramidPlotBody === body && deps.live.plotField !== null;
    if (loaded && (deps.live.plot !== null || deps.live.plotMeshLoading)) return;
    const aim = deps.descentAnchorHandle.descentAnchor();
    if (aim === null) return;
    const centre = deps.pyramidView.tileHeightProbe(aim.lat, aim.lon, false);
    if (centre === null) return;
    if (loaded && centre.level <= (pyramidPlotLevel ?? -1)) return;
    const src = deps.pyramidView.tileSource?.manifest()?.roles.get('height')?.source ?? null;
    pyramidPlotBuilding = true;
    // The same latch the claim path holds while its own field is on the wire:
    // the SURFACE build reads it to tell a survey still being scored from one
    // it can adopt whole, and the card reads it for ACQUIRING TERRAIN.
    plotLoading = true;
    loadStarted();
    try {
      const slice = deps.plotBuild.frameSlices();
      const built = await buildPyramidPlotField({
        bodyId: body,
        datumM: leafDatumM(),
        centreLatDeg: aim.lat,
        centreLonDeg: aim.lon,
        probe: (lat, lon) => {
          const got = deps.pyramidView.tileHeightProbe(lat, lon, false);
          return got === null || !pyramidPlotFlat ? got : { h: 0, level: got.level };
        },
        gsdM: (level) => heightGsd(level, leafDatumM()),
        albedo: leafOrthoOff || !deps.pyramidView.tileSource?.manifest()?.roles.has('albedo')
          ? undefined : leafAlbedoSampler(),
        // The retry ladder's own ceiling bounds the wait: imagery that never
        // draws gives flat-tint ground rather than no ground.
        albedoWait: pyramidPlotRetryMs < PYRAMID_PLOT_RETRY_MAX_MS,
        measuredFraction: deps.pyramidView.tileDrawn.get(centre.id)?.provenance.measured_fraction ?? 1,
        provenance: {
          product_id: src?.product ?? `${body} global height pyramid`,
          data_set_id: src?.product ?? '',
          url: src?.url ?? '',
          instrument: src?.instrument ?? '',
          rights: src?.rights ?? '',
          note: 'Resampled from this body\'s own height pyramid under the '
            + 'landing point; no region is baked here.',
        },
        slice,
        live: () => !deps.live.disposed && ladderBody() === body && site() === undefined
          && deps.live.plot === null,
      });
      if (built === null || deps.live.disposed || ladderBody() !== body) {
        if (built === null) {
          pyramidPlotRetryMs = Math.min(
            pyramidPlotRetryMs === 0 ? PYRAMID_PLOT_RETRY_MIN_MS : pyramidPlotRetryMs * 2,
            PYRAMID_PLOT_RETRY_MAX_MS,
          );
          pyramidPlotNextTryMs = performance.now() + pyramidPlotRetryMs;
        } else {
          // Dropped before `ensurePlotMesh` ever saw it, so nobody else owns this bitmap.
          void built.field.albedoPromise?.then((bitmap) => bitmap.close()).catch(() => {});
        }
        return;
      }
      deps.plotBuild.releasePlot();
      deps.live.plotField = built.field;
      pyramidPlotBody = body;
      pyramidPlotLevel = built.level;
      pyramidPlotHoles = built.holes;
      pyramidPlotFor = descentFor;
      pyramidPlotRetryMs = 0;
      pyramidPlotNextTryMs = 0;
      // The same sentinel every reader of `claimUrl` treats as "ground is
      // loaded"; nothing fetches from it, because the meta names no files.
      claimUrl = `pyramid:${body}`;
      claimWantUrl = claimUrl;
      const subs = await surveyPlotField(built.field,
        () => !deps.live.disposed && deps.live.plotField === built.field);
      if (subs === null) return;
      deps.live.plotSubs = subs;
      deps.live.selectedSub = null;
      deps.selection.applySelection();
      void deps.siteGround.resolveAddresses();
    } catch (e) {
      plotError = e instanceof Error ? e.message : String(e);
    } finally {
      pyramidPlotBuilding = false;
      plotLoading = false;
      loadFinished();
      if (!deps.live.disposed) {
        applySun();
        deps.overlayView.renderOverlay();
      }
    }
  }

  /**
   * Hold the tile stream down while a read that outranks it is outstanding.
   *
   * Reference counted, because two claims can overlap on a body swap, and on a
   * ceiling because a read that never settles must not leave the ground the
   * player is looking at frozen: the hold is a scheduling preference, not a
   * lock. The returned release is idempotent.
   */
  /** The depth clamp the hold runs under, or none under `--inject ring-first`. */
  function claimHoldLevel(): number | undefined {
    return ringHoldOff ? undefined : CLAIM_HOLD_MAX_LEVEL;
  }
  function holdTileFetches(): () => void {
    if (claimPriorityOff) return () => { /* the control: no hold at all */ };
    // A pending ensureSites().then() can still call loadClaim after dispose()
    // has already closed the queue; don't arm a timer nobody clears.
    if (deps.live.disposed) return () => { /* dead mount, nothing to hold */ };
    return descentQueue.enter(DESCENT_RUNG.CLAIM);
  }

  /**
   * Whatever the ring ahead has on the wire, given back.
   *
   * `ringSite` is cleared here and not in the abandoned read's own `finally`,
   * which does not run until its fetch rejects: a caller that retargets has to
   * see the slot free on the line after this one.
   */
  function releaseGroundRing(): void {
    warmGen += 1;
    ringAbort?.abort();
    ringAbort = null;
    ringSite = null;
    // The commitment goes with the ring: a focus that has moved off the ground
    // a press resolved is a focus warm again, stream and all.
    ringCommitted = null;
  }
  /**
   * What a committed landing starts, at the press rather than at the LOCAL
   * crossing, which on a throttled pipe is most of a minute later.
   *
   * The claim and the ring around the committed site, both through
   * `descentQueue`, so the ring cannot get in front of the claim the
   * walkaround's own ground is built from. Above the ORBIT floor a tile is
   * coarser than the globe map already on the canvas (`tileStreamAltM`), so
   * the ring is read into the browser's cache rather than streamed: what it
   * buys is the descent finding those bytes in hand at the floor.
   */
  function prefetchDescent(): void {
    // Every control that puts the pre-lane first descent back turns this off
    // with it: a claim started at the press has no rung to be late at, and the
    // press stands the focus's own reads down the way it used to.
    if (deps.live.disposed || prefetchOff || claimTierLate || claimPriorityOff) {
      releaseGroundRing();
      return;
    }
    void deps.siteChips.ensureSites().then(() => {
      const url = siteUrl();
      const target = site();
      if (deps.live.disposed) return;
      if (url === null || target === undefined) {
        pressPointRing();
        return;
      }
      if (claimAskedAtTier === null) claimAskedAtTier = state.tier;
      // What the focus left for this press to find. `other-site` is the yield:
      // `loadClaim` takes this url and the warm's own read stands down.
      landAskFound = claimUrl === url ? 'done'
        : plotLoading && claimWantUrl === url ? 'in-flight'
          : warmClaimUrl !== null && warmClaimUrl !== url ? 'other-site' : 'cold';
      // A ring the focus started around the ground this press resolved IS the
      // descent's ring, and runs on. Only one aimed elsewhere stands down: it
      // is reading tiles for a landing nobody committed to.
      if (pressRingOff) {
        // The press side deleted: a ring the focus started is abandoned the
        // way it was before this lane, and the press starts none. The claim
        // below still goes out here.
        releaseGroundRing();
        ringAtPress = 'off';
        void loadClaim(url);
        return;
      }
      const ring = siteRing(target);
      const gen = commitRing(ring);
      void loadClaim(url);
      startCommittedRing(ring, gen);
    });
  }
  /** The press's side of a ring: one aimed elsewhere stands down, one on this
   *  ground runs on. Returns the generation the ring's reads belong to. */
  function commitRing(ring: RingTarget): number {
    if (ringSite !== null && ringSite !== ring.id) {
      ringAtPress = 'retargeted';
      releaseGroundRing();
    } else if (ringSite === ring.id) {
      ringAtPress = 'kept';
    }
    // From here this ground outranks the tile stream: whichever of the two
    // started the ring, it is reading what the player committed to.
    ringCommitted = ring.id;
    return warmGen;
  }
  function startCommittedRing(ring: RingTarget, gen: number): void {
    if (ringAheadOff) { ringAtPress = 'off'; return; }
    if (ringSite === null && !ringDone.has(ring.id)) {
      ringAtPress = 'started';
      void readGroundRing(ring, gen);
    }
  }
  /**
   * A press on ground no site surveys. There is no claim to ask for, but the
   * descent knows where the camera is coming down, so the ring is read there.
   */
  function pressPointRing(): void {
    if (pyramidRingOff) return;
    const body = ladderBody();
    const at = pressRingOff ? null : landingPoint(body, false);
    if (at === null) {
      releaseGroundRing();
      if (pressRingOff) ringAtPress = 'off';
      return;
    }
    const ring = pointRing(body, at.lat, at.lon);
    startCommittedRing(ring, commitRing(ring));
  }
  const siteRing = (s: Site): RingTarget =>
    ({ id: s.id, body: s.body, lat: s.center_lat_deg, lon: s.center_lon_deg });
  /** Keyed on the deepest ring level's centre tile: every point inside it
   *  reads the same tiles. */
  function pointRing(body: string, lat: number, lon: number): RingTarget {
    const deepest = WARM_TILE_LEVELS[WARM_TILE_LEVELS.length - 1] ?? 0;
    const k = tileForLatLon(body, (lat * Math.PI) / 180, (lon * Math.PI) / 180, deepest);
    return { id: `${body}@${k.face}/${k.level}/${k.x}/${k.y}`, body, lat, lon };
  }
  /**
   * Where a body with no surveyed site would be landed on: the descent's own
   * anchor, or the ground under the ship. Null on a body with no ground to
   * land on, which has no pyramid to ring.
   */
  function landingPoint(body: string, preferShip: boolean): { lat: number; lon: number } | null {
    if (!(deps.bodyReaders.rowById(body)?.availability.descend ?? false)) return null;
    if (deps.bodyReaders.corridorFor(body) !== null) return null;
    const track = shipTrack !== null && shipTrack.bodyId === body
      ? { lat: shipTrack.lat, lon: shipTrack.lon } : null;
    if (preferShip && track) return track;
    const aim = ladderBody() === body ? deps.descentAnchorHandle.descentAnchor() : null;
    return aim ?? track;
  }
  /** The ring's centre tile at each level, as `<body>/tiles/<face>/<level>/<x>/<y>`. */
  function ringTilesUnder(body: string, lat: number, lon: number): string[] {
    return WARM_TILE_LEVELS.map((level) => {
      const k = tileForLatLon(body, (lat * Math.PI) / 180, (lon * Math.PI) / 180, level);
      return `${body}/tiles/${k.face}/${k.level}/${k.x}/${k.y}`;
    });
  }
  /** How long a focus has to hold before its ground is fetched. Flicking
   *  through the orrery focuses a dozen bodies and wants none of their ground. */
  const WARM_SETTLE_MS = 1500;
  /**
   * The levels the first bands under the ORBIT floor ask for around the point.
   *
   * The globe wears the pyramid's own L1 (`tileStreamAltM`), so L2 is the first
   * level that beats what is already on the canvas and L3 is the band under it.
   */
  const WARM_TILE_LEVELS = [2, 3];
  /** Separation between two body points, degrees. */
  function arcDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const d = Math.PI / 180;
    const c = Math.sin(lat1 * d) * Math.sin(lat2 * d)
      + Math.cos(lat1 * d) * Math.cos(lat2 * d) * Math.cos((lon2 - lon1) * d);
    return (Math.acos(Math.min(1, Math.max(-1, c))) * 180) / Math.PI;
  }
  /**
   * The site a press from where the ship is would come down on, as far as a
   * focus can know it: the surveyed site nearest the ship's ground track, and
   * the body's own open site when no ship is over it.
   */
  function warmTargetSite(body: string): Site | undefined {
    const rows = deps.siteRegistry.SITES.filter((s) => s.body === body);
    if (rows.length === 0) return undefined;
    const open = body === deps.siteRegistry.siteBodyId ? site() : undefined;
    // No site open on a body that has them is a jump to ground none surveys: its claim would stand in for that ground.
    if (body === deps.siteRegistry.siteBodyId && open === undefined) return undefined;
    let target = open ?? rows[0];
    const track = shipTrack !== null && shipTrack.bodyId === body ? shipTrack : null;
    if (track && rows.length > 1) {
      let bestDeg = Infinity;
      for (const row of rows) {
        const deg = arcDeg(track.lat, track.lon, row.center_lat_deg, row.center_lon_deg);
        if (deg < bestDeg) { bestDeg = deg; target = row; }
      }
    }
    // The control: ground the press will not resolve, so its own ask has to go.
    if (warmWrongSite && target) {
      return rows.find((r) => r.id !== target.id) ?? target;
    }
    return target;
  }
  /**
   * Start the ground's reads for the body in focus, so a descent from here
   * finds the claim loaded rather than starting it.
   *
   * Called from every state application and from the sim's own focus, so it is
   * keyed on what it is already warming and does nothing on the frames where
   * that has not moved.
   */
  function armGroundWarm(): void {
    if (deps.live.disposed || focusWarmOff) return;
    // The Atlas's own focus first, then the body the ship is at: from the
    // bridge nothing is focused here and a press is still a landing on it.
    // Out in cruise `state.bodyId` is the strongest pull rather than a focus.
    const body = (deps.worldHostState.externalWorld && !warmPull ? shipTrack?.bodyId : undefined)
      ?? state.bodyId ?? shipTrack?.bodyId ?? null;
    if (body === null) { releaseGroundWarm(); return; }
    if (body === warmBody) {
      // The ground track moved nearer other ground: the warm follows it.
      if (warmSiteId === null) return;
      const next = warmTargetSite(body)?.id ?? null;
      if (next === null || next === warmSiteId) return;
    }
    releaseGroundWarm();
    warmBody = body;
    warmSettle = setTimeout(() => {
      warmSettle = null;
      startGroundWarm(body);
    }, WARM_SETTLE_MS);
  }
  /**
   * Everything the warm holds, given back.
   *
   * An unfocus while the ship is still over the body is not one of these: the
   * body it reads off is the ship's own when the Atlas has no focus of its
   * own, so the warm outlives closing the view.
   */
  function releaseGroundWarm(): void {
    if (warmSettle !== null) { clearTimeout(warmSettle); warmSettle = null; }
    releaseGroundRing();
    // A warm claim on a body no ladder is on is nobody's: `syncBody` releases
    // the plot when the LADDER's body changes, and a warm the ship's focus
    // asked for never reached that. The claim can still be in flight when
    // focus leaves - nothing else bumps its token for this path - so an
    // abandoned read still lands and publishes unless dropped here too.
    if (state.bodyId === null && warmClaimUrl !== null) {
      if (claimUrl === warmClaimUrl) deps.plotBuild.releasePlot();
      else if (plotLoading && claimWantUrl === warmClaimUrl) {
        claimGen += 1;
        if (!noWantClear) claimWantUrl = null;
      }
    }
    warmBody = null;
    warmSiteId = null;
    warmClaimUrl = null;
    warmAskedAltM = null;
    warmAskedMs = null;
    warmClaimReadyMs = null;
  }
  function startGroundWarm(body: string): void {
    if (deps.live.disposed || focusWarmOff || warmBody !== body) return;
    const gen = warmGen;
    void deps.siteChips.ensureSites().then(() => {
      if (deps.live.disposed || warmBody !== body || warmGen !== gen) return;
      const target = warmTargetSite(body);
      // A body with no baked site has no claim to warm, only the ground under
      // the landing point; the press re-keys it if it comes down elsewhere.
      if (!target) {
        const at = pyramidRingOff ? null : landingPoint(body, deps.worldHostState.externalWorld);
        if (at !== null) void readGroundRing(pointRing(body, at.lat, at.lon), gen);
        return;
      }
      warmSiteId = target.id;
      const url = dtmUrl(target.id);
      warmClaimUrl = url;
      // `state.bodyId` alone cannot stand in for "the Atlas panel is open
      // here": cruise's own `setWorldBody` sets it too, camera untouched, so
      // this also excludes the ship-track-only path where `externalWorld` is
      // the cruise backdrop rather than an explicit navigation.
      warmAskedAltM = !deps.worldHostState.externalWorld && state.bodyId === body ? altitude() : null;
      warmAskedMs = performance.now();
      if (claimAskedAtTier === null) claimAskedAtTier = state.tier;
      void loadClaim(url).then(() => {
        if (deps.live.disposed || warmGen !== gen || claimUrl !== url) return;
        warmClaimReadyMs = performance.now();
        void readGroundRing(siteRing(target), gen);
      });
    });
  }
  /**
   * Where a ring read is charged: before any press it is the focus spending on
   * ground nobody has committed to, after it the descent's own.
   */
  const ringLedger = (): { requests: number; bytes: number; tiles: number } =>
    (landPressMs === null ? warmSpend : ringSpend);
  /**
   * The ring the first bands under the floor will ask for, read into the
   * browser's own cache.
   *
   * The BYTES and nothing else: no tile, no mesh and no tree, so there is
   * nothing here for the pyramid to own or for this module to dispose, and the
   * stamped url is the one `tileSource` will ask for. One read at a time
   * behind the claim on `descentQueue`.
   *
   * A ring for ground nobody has committed to stands down the moment the
   * pyramid's own stream starts: from there the tree's traversal is better
   * aimed than a ring around some site's centre. A ring for the site a press
   * resolved does not - that ring IS the ground being landed on and is the
   * rung above the stream, which the queue holds rather than carries, so one
   * read at a time beside it is the whole of what it costs.
   *
   * Started by a focus that settles and by the press, whichever comes first;
   * the second of the two finds the site already in `ringSite` or `ringDone`
   * and leaves the first alone.
   */
  async function readGroundRing(target: RingTarget, gen: number): Promise<void> {
    if (deps.live.disposed || ringAheadOff || ringSite !== null || ringDone.has(target.id)) return;
    ringSite = target.id;
    const ctrl = new AbortController();
    ringAbort = ctrl;
    const body = target.body;
    let whole = false;
    try {
      const res = await descentQueue.run(DESCENT_RUNG.RING,
        () => fetch(pyramidUrl(DTM_TILE_BASE, body), { signal: ctrl.signal }));
      ringLedger().requests += 1;
      if (!res.ok) return;
      const manifest = parsePyramidManifest(await res.json());
      const latRad = (target.lat * Math.PI) / 180;
      const lonRad = (target.lon * Math.PI) / 180;
      for (const level of WARM_TILE_LEVELS) {
        const centre = tileForLatLon(body, latRad, lonRad, level);
        for (const key of [centre, ...neighboursOf(centre)]) {
          for (const role of rolesForTile(manifest, key)) {
            if (role === 'artificial' && !deps.pyramidView.artificialLayerOn()) continue;
            if (deps.live.disposed || warmGen !== gen) return;
            if (deps.pyramidView.tilesActive() && ringCommitted !== target.id) return;
            const url = tileUrl(DTM_TILE_BASE, key, role, manifest.roles.get(role)?.stamp ?? null);
            const tile = await descentQueue.run(DESCENT_RUNG.RING,
              () => fetch(url, { signal: ctrl.signal }));
            const ledger = ringLedger();
            ledger.requests += 1;
            // A tile this pyramid does not hold is index.html at 200 from the
            // dev server, which is not bytes this read bought.
            if (!tile.ok || (tile.headers.get('content-type') ?? '').includes('html')) continue;
            ledger.bytes += (await tile.arrayBuffer()).byteLength;
            ledger.tiles += 1;
          }
        }
      }
      whole = true;
    } catch { /* an abandoned or refused read is not something the player sees */ } finally {
      if (ringAbort === ctrl) ringAbort = null;
      if (ringSite === target.id) ringSite = null;
      if (whole) ringDone.add(target.id);
    }
  }

  return {
    loadClaim,
    surveyRings,
    ensurePyramidPlot,
    claimHoldLevel,
    releaseGroundRing,
    prefetchDescent,
    arcDeg,
    armGroundWarm,
    ringHoldOff,
    ringUnbudgeted,
    ringHole,
    ringChurn,
    fetchAtCrossing,
    prefetchOff,
    focusWarmOff,
    ringAheadOff,
    pressRingOff,
    pyramidRingOff,
    ringTilesUnder,
    warmWrongSite,
    noWantClear,
    noReleaseClear,
    noClaimRetarget,
    noWantRestore,
    pyramidPlotOff,
    pyramidPlotFlat,
    plotKeepBody,
    plotReleaseOnLand,
    warmSpend,
    ringSpend,
    ringDone,
    descentQueue,
    state: {
      get plotLoading() { return plotLoading; },
      get claimPriorityOff() { return claimPriorityOff; },
      set claimPriorityOff(v) { claimPriorityOff = v; },
      get claimTierLate() { return claimTierLate; },
      set claimTierLate(v) { claimTierLate = v; },
      get pyramidPlotBody() { return pyramidPlotBody; },
      set pyramidPlotBody(v) { pyramidPlotBody = v; },
      get pyramidPlotLevel() { return pyramidPlotLevel; },
      set pyramidPlotLevel(v) { pyramidPlotLevel = v; },
      get pyramidPlotHoles() { return pyramidPlotHoles; },
      set pyramidPlotHoles(v) { pyramidPlotHoles = v; },
      get pyramidPlotFor() { return pyramidPlotFor; },
      set pyramidPlotFor(v) { pyramidPlotFor = v; },
      get descentFor() { return descentFor; },
      set descentFor(v) { descentFor = v; },
      get pyramidPlotBuilding() { return pyramidPlotBuilding; },
      get pyramidPlotRetryMs() { return pyramidPlotRetryMs; },
      set pyramidPlotRetryMs(v) { pyramidPlotRetryMs = v; },
      get pyramidPlotNextTryMs() { return pyramidPlotNextTryMs; },
      set pyramidPlotNextTryMs(v) { pyramidPlotNextTryMs = v; },
      get claimAskedAtTier() { return claimAskedAtTier; },
      set claimAskedAtTier(v) { claimAskedAtTier = v; },
      get claimStartAltM() { return claimStartAltM; },
      get claimWantUrl() { return claimWantUrl; },
      set claimWantUrl(v) { claimWantUrl = v; },
      get claimQueuedUrl() { return claimQueuedUrl; },
      get warmBody() { return warmBody; },
      get warmSiteId() { return warmSiteId; },
      get warmSettle() { return warmSettle; },
      set warmSettle(v) { warmSettle = v; },
      get warmAskedAltM() { return warmAskedAltM; },
      get warmAskedMs() { return warmAskedMs; },
      get warmClaimReadyMs() { return warmClaimReadyMs; },
      get landPressMs() { return landPressMs; },
      set landPressMs(v) { landPressMs = v; },
      get ringSite() { return ringSite; },
      get ringCommitted() { return ringCommitted; },
      get ringAtSurface() { return ringAtSurface; },
      set ringAtSurface(v) { ringAtSurface = v; },
      get ringAtPress() { return ringAtPress; },
      get landAskFound() { return landAskFound; },
      get shipTrack() { return shipTrack; },
      set shipTrack(v) { shipTrack = v; },
      get claimUrl() { return claimUrl; },
      set claimUrl(v) { claimUrl = v; },
      get claimGen() { return claimGen; },
      set claimGen(v) { claimGen = v; },
      get plotError() { return plotError; },
      set plotError(v) { plotError = v; },
    },
  };
}
